import React, { useState, useContext } from 'react';
import { ThemeContext } from 'styled-components';
import { find, map } from 'lodash';
import { format, eachDayOfInterval, isSameDay, addWeeks, parseISO } from 'date-fns';
import {
  Box,
  Text,
  Heading,
  Button,
  ButtonAppearance,
  ButtonSize,
  Icon,
  IconSize,
  Theme,
  useMediaQuery,
  belowBreakpoint,
} from '@bechtel/gds';
import { useAppUser } from '@bechtel/client-common';
import {
  ShiftLogStyled,
  ShiftLogHeaderStyled,
  ShiftLogWeekStyled,
  ShiftLogBodyStyled,
  ShiftLogItemStyled,
  ShiftLogCommentStyled,
  ShiftLogCommentCountStyled,
  TabButtonStyled,
} from './styled';
import { Trans } from '@lingui/macro';

export const ShiftLog = () => {
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isWideScreen = useMediaQuery(belowBreakpoint(breakpoints.widescreen));
  const { user } = useAppUser();
  const today = new Date();
  const [selectedDay, setSelectedDay] = useState(today);
  const [weeksToIncrement, setWeekToIncrement] = useState(0);
  const currentProjectWeek = find(user?.project?.projectConfiguration?.projectWeeks, (projectWeek) =>
    projectWeek?.isCurrent ? true : false
  );
  const weekStartDay = currentProjectWeek?.startDate
    ? addWeeks(parseISO(currentProjectWeek?.startDate), weeksToIncrement)
    : null;
  const weekEndDay = currentProjectWeek?.endDate
    ? addWeeks(parseISO(currentProjectWeek?.endDate), weeksToIncrement)
    : null;
  const weekDays = weekStartDay && weekEndDay ? eachDayOfInterval({ start: weekStartDay, end: weekEndDay }) : null;

  return (
    <ShiftLogStyled>
      <ShiftLogHeaderStyled>
        <Heading style={{ flex: 1 }} weight="600" size={isWideScreen ? '500' : '600'}>
          <Trans>Shift Log</Trans>
        </Heading>
        <Box mr={{ sm: '500' }}>
          <Text size={isWideScreen ? '300' : '400'} color={'grey.600'}>
            <Trans>Week</Trans> {(currentProjectWeek?.weekNumber || 0) + weeksToIncrement}:&nbsp;
            {weekStartDay ? format(weekStartDay, 'MMM dd') : ''} - {weekEndDay ? format(weekEndDay, 'MMM dd') : ''}
          </Text>
        </Box>
        <Box>
          <Button size={ButtonSize.SMALL} appearance={ButtonAppearance.SECONDARY}>
            <Box mr={{ sm: '300' }}>
              <Icon icon="pen-square" size={IconSize.MEDIUM} fillColor="blue.800" />
            </Box>
            <Text size={'300'} color={'grey.1000'}>
              <Trans>Add/Edit Notes</Trans>
            </Text>
          </Button>
        </Box>
      </ShiftLogHeaderStyled>
      <ShiftLogWeekStyled>
        <Box mr={{ sm: '300' }} onClick={() => setWeekToIncrement(weeksToIncrement - 1)}>
          <Icon icon="chevron-circle-left" size={IconSize.LARGE} fillColor="blue.800" />
        </Box>
        {weekDays &&
          map(weekDays, (weekDay, index) => {
            return (
              <TabButtonStyled
                key={index}
                selected={isSameDay(weekDay, selectedDay)}
                onClick={() => setSelectedDay(weekDay)}
              >
                <Box flexDirection={{ sm: 'column' }}>
                  <Text size={isWideScreen ? '250' : '300'}>{format(weekDay, 'EEE')}</Text>
                  <Text size={isWideScreen ? '500' : '600'}>{format(weekDay, 'dd')}</Text>
                </Box>
              </TabButtonStyled>
            );
          })}

        <Box mr={{ sm: '300' }} onClick={() => setWeekToIncrement(weeksToIncrement + 1)}>
          <Icon icon="chevron-circle-right" size={IconSize.LARGE} fillColor="blue.800" />
        </Box>
      </ShiftLogWeekStyled>
      <ShiftLogBodyStyled>
        <ShiftLogItemStyled>
          <Icon icon="clipboard" fillColor="blue.800" size={IconSize.LARGE}></Icon>
          <Box style={{ flex: '1' }} ml={{ sm: '600' }}>
            <Text size={isWideScreen ? '300' : '400'}>
              <Trans>General</Trans>
            </Text>
          </Box>
          <ShiftLogCommentStyled>
            <Icon icon="comment-alt" iconStyle="solid" fillColor="green.500" size={IconSize.LARGE}></Icon>
            <ShiftLogCommentCountStyled>
              <Text size="400">01</Text>
            </ShiftLogCommentCountStyled>
          </ShiftLogCommentStyled>
          <Icon icon="chevron-right" size={IconSize.LARGE}></Icon>
        </ShiftLogItemStyled>
        <ShiftLogItemStyled>
          <Icon icon="hard-hat" fillColor="blue.800" size={IconSize.LARGE}></Icon>
          <Box style={{ flex: '1' }} ml={{ sm: '600' }}>
            <Text size="400">
              <Trans>Safety</Trans>
            </Text>
          </Box>
          <ShiftLogCommentStyled>
            <Icon icon="comment-alt" iconStyle="solid" fillColor="green.500" size={IconSize.LARGE}></Icon>
            <ShiftLogCommentCountStyled>
              <Text size="400">01</Text>
            </ShiftLogCommentCountStyled>
          </ShiftLogCommentStyled>
          <Icon icon="chevron-right" size={IconSize.LARGE}></Icon>
        </ShiftLogItemStyled>
        <ShiftLogItemStyled>
          <Icon icon="box-check" fillColor="blue.800" size={IconSize.LARGE}></Icon>
          <Box style={{ flex: '1' }} ml={{ sm: '600' }}>
            <Text size="400">
              <Trans>Work Package Turnover</Trans>
            </Text>
          </Box>
          <ShiftLogCommentStyled>
            <Icon icon="comment-alt" iconStyle="solid" fillColor="green.500" size={IconSize.LARGE}></Icon>
            <ShiftLogCommentCountStyled>
              <Text size="400">01</Text>
            </ShiftLogCommentCountStyled>
          </ShiftLogCommentStyled>
          <Icon icon="chevron-right" size={IconSize.LARGE}></Icon>
        </ShiftLogItemStyled>
      </ShiftLogBodyStyled>
    </ShiftLogStyled>
  );
};
